/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import BatchInfoCard from './BatchInfoCard';
import '../styles/BarCode.css';

const Barcode = () => {
  const { po_number, sku_code } = useParams();
  const [item, setItem] = useState(null);
  const [lastScannedCode, setLastScannedCode] = useState('');
  const [barcodeValue, setBarcodeValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef(null);
  const processingRef = useRef(false);
  const lastScannedCodeRef = useRef('');
  const navigate = useNavigate();
  const location = useLocation();

  const { token, userRole } = useAuth();

  const isLikelyBatchQr = (code, expectedSkuCode) => {
    if (!code || !expectedSkuCode) return false;
    const prefix = `${expectedSkuCode}-`;
    if (!code.startsWith(prefix)) return false;
    const suffix = code.slice(prefix.length);
    return /^[0-9A-Za-z]{4}$/.test(suffix);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    processingRef.current = false;
  };

  const toggleInputMode = () => {
    setIsManualMode(!isManualMode);
    setBarcodeValue('');
    setManualInput('');
    setErrorMessage('');
    lastScannedCodeRef.current = '';
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualInput.trim()) {
      setErrorMessage('Por favor ingrese un código de barras');
      return;
    }

    setIsSubmitting(true);
    processBarcode(manualInput.trim());
    setIsSubmitting(false);
  };

  const processBarcode = async (code) => {
    if (processingRef.current) return;
    if (code === lastScannedCodeRef.current && errorMessage) return;
    processingRef.current = true;
    lastScannedCodeRef.current = code;
    setErrorMessage('');

    // Get accumulated weight from location.state (for multi-scan kg products)
    const accumulatedWeight = location.state?.accumulatedWeight || 0;

    if (item?.format_unit_kg) {
      if (code.includes(sku_code)) {
        const lastSixDigits = code.slice(-6);
        const firstFiveOfLastSix = lastSixDigits.slice(0, 5);
        const weightNumber = parseFloat(`${firstFiveOfLastSix.slice(0, 2)}.${firstFiveOfLastSix.slice(2)}`);
        const formattedNumber = weightNumber.toFixed(3);
        navigate(`/picker/${po_number}/${item.cart_code}/${sku_code}/RACK-SHELF/${formattedNumber}`, {
          state: {
            accumulatedWeight: accumulatedWeight,
            orderData: location.state?.orderData
          }
        });
      } else {
        showError(`Código no válido para producto por peso. El código de barras debe contener el SKU ${sku_code}. Escaneado: ${code}.`);
      }
      return;
    }

    // Accept either the legacy plain barcode (un-relabeled stock) or the new
    // <sku>-XXXX batch QR — strict match against the FEFO-picked exp date.
    if (code === item?.sku_bar_code) {
      navigate(`/picker/${po_number}/${item.cart_code}/${sku_code}/RACK-SHELF/${code}`);
      return;
    }

    const expectedBatch = item?.picking_positions?.batch || null;
    const expectedExp = item?.picking_positions?.expiration_date || null;

    if (isLikelyBatchQr(code, sku_code)) {
      try {
        const response = await fetch(`${API_BASE}/decode-batch-qr/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ text: code }),
        });
        if (!response.ok) {
          showError('Error al validar el QR con el servidor. Intente nuevamente.');
          return;
        }
        const data = await response.json();
        if (!data.valid) {
          showError(`QR no reconocido. Verifique que la etiqueta esté bien impresa. Escaneado: ${code}.`);
          return;
        }
        if (data.sku !== sku_code) {
          showError(`El QR pertenece a otro producto. Esperado: ${sku_code}. El QR es del SKU: ${data.sku}.`);
          return;
        }
        if (!expectedExp) {
          showError('No se puede validar el QR: el lote a recoger no tiene fecha de vencimiento registrada. Avise al supervisor.');
          return;
        }
        if (data.exp_date !== expectedExp) {
          showError(
            `El QR no corresponde al lote a recoger. ` +
            `Vence en QR: ${formatDate(data.exp_date)}. ` +
            `Lote a recoger: ${expectedBatch || sku_code} (vence ${formatDate(expectedExp)}).`
          );
          return;
        }
        navigate(`/picker/${po_number}/${item.cart_code}/${sku_code}/RACK-SHELF/${code}`);
        return;
      } catch (err) {
        console.error('Error decoding batch QR:', err);
        showError('Error de red al validar el QR. Verifique su conexión e intente nuevamente.');
        return;
      }
    }

    const expectedParts = [];
    if (item?.sku_bar_code) expectedParts.push(`código de barras ${item.sku_bar_code}`);
    expectedParts.push(`QR ${expectedBatch || `${sku_code}-XXXX`}`);
    showError(`Código no reconocido. Escaneado: ${code}. Esperado: ${expectedParts.join(' o ')}.`);
  };

  useEffect(() => {
    if (location.state?.orderData) {
      const foundItem = location.state.orderData.items.find(item => item.sku_code === sku_code);
      if (foundItem) {
        const cart = location.state.orderData.carts?.find(cart =>
          userRole === 'frozen' ? cart.code.startsWith('BASKET') : cart.code.startsWith('CART')
        );
        setItem({ ...foundItem, cart_code: cart?.code });
      } else {
        console.error(`No item found for SKU: ${sku_code}`);
      }
    } else {
      // If no order data in state, fetch it
      const fetchData = async () => {
        try {
          const response = await fetch(`${API_BASE}/get-po/?po_number=${po_number}`, {
            headers: { Authorization: `Token ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            const foundItem = data.items.find(item => item.sku_code === sku_code);
            if (foundItem) {
              const cart = data.carts?.find(cart =>
                userRole === 'frozen' ? cart.code.startsWith('BASKET') : cart.code.startsWith('CART')
              );
              setItem({ ...foundItem, cart_code: cart?.code });
            } else {
              console.error(`No item found for SKU: ${sku_code}`);
            }
          } else {
            console.error('Failed to fetch PO data', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Error:', error);
        }
      };
      fetchData();
    }
  }, [token, po_number, sku_code, userRole, location.state]);

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader();
    let activeStream;

    const initializeScanner = async () => {
      if (isManualMode) {
        if (activeStream) {
          activeStream.getTracks().forEach((track) => track.stop());
        }
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });

        activeStream = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        codeReader.decodeFromVideoDevice(null, videoRef.current, (result, error) => {
          if (result) {
            const videoElement = videoRef.current;
            const videoWidth = videoElement.videoWidth;
            const videoHeight = videoElement.videoHeight;

            const scanningBox = {
              x: videoWidth * 0.1,
              y: videoHeight * 0.4,
              width: videoWidth * 0.8,
              height: videoHeight * 0.2,
            };

            const resultPoints = result.resultPoints;
            const isInsideBox = resultPoints.every(
              (point) =>
                point.x >= scanningBox.x &&
                point.x <= scanningBox.x + scanningBox.width &&
                point.y >= scanningBox.y &&
                point.y <= scanningBox.y + scanningBox.height
            );

            if (isInsideBox) {
              console.log('Barcode detected inside box:', result.getText());
              setBarcodeValue(result.getText());
              processBarcode(result.getText());
            } else {
              console.log('Barcode detected outside box. Ignored.');
            }
          }
        });
      } catch (error) {
        console.error('Error initializing scanner:', error);
        setErrorMessage('Failed to access the camera. Please check your permissions.');
      }
    };

    initializeScanner();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
      codeReader.reset();
    };
  }, [item, lastScannedCode, po_number, sku_code, navigate, isManualMode]);

  return (
    <div className="scanner-page">
      <h1 className="barcode-title">Barcode Scanner</h1>

      {/* Item details */}
      {item && (
        <>
          <h2 className="barcode-item-name">{item.sku_name}</h2>
          <h3 className="barcode-sku-code">SKU: {item.sku_code}</h3>
          <BatchInfoCard pickingPosition={item.picking_positions} />
        </>
      )}

      {/* Back button */}
      <button
        onClick={() => navigate(`/picker/${po_number}/${item?.cart_code}`)}
        className="back-button"
      >
        ←
      </button>

      <div className="input-mode-toggle">
        <button
          className={`mode-button ${!isManualMode ? 'active' : ''}`}
          onClick={() => !isManualMode ? null : toggleInputMode()}
        >
          Escanear
        </button>
        <button
          className={`mode-button ${isManualMode ? 'active' : ''}`}
          onClick={() => isManualMode ? null : toggleInputMode()}
        >
          Entrada Manual
        </button>
      </div>

      {isManualMode ? (
        <div className="manual-input-container">
          <form onSubmit={handleManualSubmit}>
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Ingrese el código de barras"
              className="manual-input"
              autoFocus
            />
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Procesando...' : 'Confirmar'}
            </button>
          </form>
        </div>
      ) : (
        <div className="scanner-container">
          <video ref={videoRef} className="scanner-video" autoPlay playsInline></video>
          <div className="scanner-overlay">
            <p className="scanner-instructions">Mantén el código de barras dentro del marco para escanearlo</p>
            <div className="scanner-box"></div>
          </div>
        </div>
      )}

      {barcodeValue && (
        <p className="scanner-output">
          <strong>Código Escaneado:</strong> {barcodeValue}
        </p>
      )}
      {errorMessage && <p className="scanner-error">{errorMessage}</p>}
    </div>
  );
};

export default Barcode;
